import React, { useState } from 'react';
import { Container, Card, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../context/UserAuthContext';
import { FaTrashAlt, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import './Profile.css';

const DeleteAccount = () => {
  const { t } = useTranslation();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // When the page loads, automatically show the deletion confirmation modal
  useState(() => {
    setShowDeleteModal(true);
  }, []);

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    // Go back to previous page or to profile
    navigate(-1);
  };
  
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError('');
    
    try {
      // Call backend to delete user account and all associated data
      await api.delete('/users/account');
      
      // Sign out user and redirect to home page
      // This assumes the logout function is available in the useUserAuth context
      // If not, you would need to sign out the user here
      if (window.firebase && window.firebase.auth) {
        await window.firebase.auth().signOut();
      }
      
      // Show success message before redirecting
      setSuccess(t('accountDeletion.successMessage'));
      setShowDeleteModal(false);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container className="delete-account-container py-5">
      {success && (
        <Card className="text-center shadow p-4">
          <Card.Body>
            <Alert variant="success">{success}</Alert>
          </Card.Body>
        </Card>
      )}
      
      {/* Delete Account Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaExclamationTriangle className="me-2" />
            {t('accountDeletion.confirmTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('accountDeletion.confirmText')}</p>
          {error && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete} disabled={deleting}>
            <FaTimes className="me-2" />
            {t('accountDeletion.cancelButton')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteAccount}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('loading')}
              </>
            ) : (
              <>
                <FaTrashAlt className="me-2" />
                {t('accountDeletion.deleteButton')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DeleteAccount;